import { FC, useEffect, useState } from "react";
import Errors from "../../../components/errors/Errors";
import Listview from "../../../components/listview/Listview";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import useAbortController from "../../../hooks/useAbortController";
import { Members } from "../../../listsSettings/members";
import { ErrorCode, IListviewField, SearchResult, UserPermissions } from "../../../services/types";
import { getUserPermissions } from "../../../services/webapi";
import * as Fields from "./Fields";
import { Company } from "./types";
import { getActiveCompanies, getActiveCompanyFilters } from "./webapi";

const ActiveCompanies: FC = () => {
  const listviewId = "active-companies";
  const fields: IListviewField<Company>[] = [
    Fields.name,
    Fields.companyNumber,
    Fields.member,
    Fields.memberNumber,
    Fields.street,
    Fields.city,
    Fields.zip,
    Fields.province,
    Fields.country,
    Fields.phone,
    Fields.email,
  ];

  const [companies, setCompanies] = useState<SearchResult<Company> | null>(null);

  const [abortController, resetAbortController] = useAbortController();
  const [errorCode, setErrorCode] = useState<ErrorCode>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
  };

  useEffect(() => {
    loadCompanies(pagination);

    return () => {
      abortController && abortController.abort();
    };
  }, []);

  const loadCompanies = async (pagination: Pagination) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Members.Lists.Companies.InternalName,
      Members.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getActiveCompanies(pagination, abortController.signal);
        if (!abortController.signal.aborted) {
          result.data && setCompanies(result.data);
          result.error && setErrorCode(result.error.code);
        }
      }
    }
  };

  return (
    <>
      {permissions &&
        (!errorCode ? (
          <Listview
            id={listviewId}
            viewProperties={{
              schema: Members.InternalName,
              list: Members.Lists.Companies.InternalName,
              name: "active_companies",
              setListAndPage: true,
            }}
            fields={fields}
            reload={loadCompanies}
            searchResult={companies}
            getFilters={getActiveCompanyFilters}
            pagination={pagination}
            updateItems={(items) =>
              companies &&
              setCompanies({
                ...companies,
                items: items,
              })
            }
          />
        ) : (
          errorCode && <Errors errorCode={errorCode} />
        ))}
    </>
  );
};

export default ActiveCompanies;
